import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { FolderKanban, FileText, Ticket, Mountain, Puzzle, Search } from "lucide-react";

const TYPES = [
  { v: "project", label: "Project", icon: FolderKanban, api: "/projects",     getIdent: (o) => o.code },
  { v: "task",    label: "Task",    icon: FileText,     api: "/tasks",        getIdent: (o) => o.id?.slice(0, 6) },
  { v: "cr",      label: "CR",      icon: Ticket,       api: "/tickets",      getIdent: (o) => o.code },
  { v: "epic",    label: "Epic",    icon: Mountain,     api: null,            getIdent: (o) => o.identifier },
  { v: "feature", label: "Feature", icon: Puzzle,       api: null,            getIdent: (o) => o.identifier },
];
const LABEL = (v) => TYPES.find((t) => t.v === v)?.label || v;

/**
 * Lightweight slash command menu for the TipTap editor.
 *
 * How it hooks in (callers pass an `editor` ref):
 *   1. editor.on('update') fires → we inspect text before the caret
 *   2. If it matches `/task`, `/cr`, `/project` etc. we open the picker
 *   3. User picks an entity → we `editor.chain().insertContent(<link node>).run()`
 *      with a styled link pointing at the PRIZM page.
 *
 * We don't register a TipTap extension — this React-level approach is enough
 * for Phase 2 and keeps the editor code simple.
 */
export default function SlashMenu({ editor }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(null);           // when non-null, show picker list
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [slashRange, setSlashRange] = useState(null); // {from, to} to replace
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!editor) return;

    const onUpdate = () => {
      const { state } = editor;
      const { from } = state.selection;
      const text = state.doc.textBetween(Math.max(0, from - 25), from, "\n", " ");
      const m = text.match(/\/([a-z]*)$/i);
      if (!m) {
        if (open) { setOpen(false); setType(null); setResults([]); }
        return;
      }
      const token = m[1].toLowerCase();
      const start = from - m[0].length;
      setSlashRange({ from: start, to: from });

      // Position the menu just below the caret
      const coords = editor.view.coordsAtPos(from);
      setPos({ x: coords.left, y: coords.bottom + 4 });

      // Type match?
      const matchedType = TYPES.find((t) => t.v === token || token.startsWith(t.v));
      if (matchedType) {
        setType(matchedType);
        setQuery(token.slice(matchedType.v.length));
      } else {
        setType(null);
        setQuery(token);
      }
      setOpen(true);
      setActiveIdx(0);
    };

    editor.on("update", onUpdate);
    editor.on("selectionUpdate", onUpdate);
    return () => {
      editor.off("update", onUpdate);
      editor.off("selectionUpdate", onUpdate);
    };
  }, [editor, open]);

  // Lookup when type is set
  useEffect(() => {
    if (!type || !type.api) { setResults([]); return; }
    let cancelled = false;
    api.get(type.api).then((r) => {
      if (cancelled) return;
      const list = (r.data || []).filter((o) =>
        !query || (o.title || o.name || o.code || "").toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8);
      setResults(list);
    }).catch(() => setResults([]));
    return () => { cancelled = true; };
  }, [type, query]);

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (!type) {
        // Picking a type
        if (e.key === "Escape") { setOpen(false); return; }
        return;  // Let the user keep typing to filter TYPES below
      }
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(results.length - 1, i + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
      else if (e.key === "Enter" && results[activeIdx]) { e.preventDefault(); pick(results[activeIdx]); }
      else if (e.key === "Escape") { setOpen(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, type, results, activeIdx]);

  const pick = (obj) => {
    if (!editor || !slashRange || !type) return;
    const label = obj.title || obj.name || obj.code || obj.identifier || "item";
    const ident = type.getIdent(obj) || "";
    const text = ident ? `${ident} · ${label}` : label;
    const href = `/${type.v === "cr" ? "tickets" : type.v + "s"}/${obj.id}`;

    editor
      .chain()
      .focus()
      .deleteRange(slashRange)
      .insertContent([
        {
          type: "text",
          marks: [{ type: "link", attrs: { href, target: "_blank", class: "prizm-ref" } }],
          text: `🔗 ${text}`,
        },
        { type: "text", text: " " },
      ])
      .run();

    setOpen(false); setType(null); setResults([]);
  };

  const typeFiltered = query
    ? TYPES.filter((t) => t.v.startsWith(query) || t.label.toLowerCase().startsWith(query))
    : TYPES;

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 50 }}
      className="w-72 glass-strong border rounded-lg shadow-xl overflow-hidden"
      data-testid="slash-menu"
    >
      {!type ? (
        <>
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b">
            Insert PRIZM reference
          </div>
          <div className="max-h-72 overflow-y-auto">
            {typeFiltered.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground">No matching command. Try /task, /cr, /project.</div>
            )}
            {typeFiltered.map((t) => (
              <button
                key={t.v}
                onClick={() => { setType(t); setQuery(""); setActiveIdx(0); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50"
                data-testid={`slash-type-${t.v}`}
              >
                <t.icon className="w-4 h-4 text-primary" />
                <span className="flex-1">{t.label}</span>
                <code className="text-[10px] text-muted-foreground">/{t.v}</code>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="px-3 py-2 flex items-center gap-2 border-b">
            <type.icon className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs">{LABEL(type.v)}</span>
            <code className="text-[10px] text-muted-foreground ml-auto">↑↓ Enter</code>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {results.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                <Search className="w-3 h-3" /> No matches
              </div>
            ) : (
              results.map((o, i) => {
                const label = o.title || o.name || o.code || o.identifier || "item";
                const ident = type.getIdent(o) || "";
                return (
                  <button
                    key={o.id}
                    onClick={() => pick(o)}
                    data-testid={`slash-pick-${o.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                      i === activeIdx ? "bg-primary/15 text-primary" : "hover:bg-accent/50"
                    }`}
                  >
                    {ident && <code className="text-[10px] text-muted-foreground font-mono shrink-0">{ident}</code>}
                    <span className="flex-1 min-w-0 truncate">{label}</span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}
